import type { CalendarEvent } from '@/api';
import {
  addDayKey,
  addMonthKey,
  dayKeyInZone,
  dayKeyRange,
  fmtFullDateOf,
  fmtMonthDayOf,
  fmtMonthYearOf,
  fmtTimeIn,
  fmtWeekdayShortOf,
  minutesFromDayStart,
  minutesInZonedDay,
  partsOfDayKey,
  startOfMonthKey,
  startOfWeekKey,
  startOfZonedDay,
  type DayKey,
} from '@/features/calendar/zone';
import { dateLocale } from '@/utils/format';

/**
 * View-level date helpers for the calendar routes.
 *
 * All the zone-sensitive arithmetic lives in `@/features/calendar/zone`; this
 * module is the layer above it that knows about *views* — what a week is, what
 * "next" means in each mode, how a period is titled.
 *
 * The unit of a view is a `DayKey` (`"YYYY-MM-DD"`), never a `Date`. A `Date`
 * that stands for "a day" is really "midnight somewhere", and every calendar
 * off-by-one-day bug is that ambiguity cashing out. Instants stay `Date`s and
 * are only ever positioned *relative to* a day key in a named zone.
 */

export type CalendarViewMode = 'day' | 'week' | 'month' | 'agenda';

export const CALENDAR_VIEW_MODES: readonly CalendarViewMode[] = ['day', 'week', 'month', 'agenda'];

export function isCalendarViewMode(value: string | undefined): value is CalendarViewMode {
  return value === 'day' || value === 'week' || value === 'month' || value === 'agenda';
}

export const AGENDA_DAYS = 30;
/** Six weeks, so the grid never changes height between months. */
export const MONTH_CELLS = 42;
/** Drag and click-to-create both snap here (doc 06 §7). */
export const SNAP_MINUTES = 15;

export interface ViewRange {
  days: DayKey[];
  /** Inclusive first day. */
  startKey: DayKey;
  /** **Exclusive** day after the last — the range query's upper bound. */
  endKey: DayKey;
}

/**
 * How many days the Week view shows. Seven, except on a phone narrower than
 * 40rem, where it is three days *starting at the anchor* (plan 15 §9.1): seven
 * 45-px columns are unreadable, and a week that scrolls sideways is the worst
 * of both. The route is still `/calendar/week`; only the span changes.
 */
export const WEEK_DAYS = 7;
export const COMPACT_WEEK_DAYS = 3;

export function rangeFor(
  mode: CalendarViewMode,
  anchorKey: DayKey,
  weekDays: number = WEEK_DAYS,
): ViewRange {
  const days = (() => {
    if (mode === 'month') return dayKeyRange(startOfWeekKey(startOfMonthKey(anchorKey)), MONTH_CELLS);
    if (mode === 'week') {
      // A short span is not a week, so it does not snap back to Sunday: it
      // starts where you are, which is what "the next three days" means.
      return weekDays === WEEK_DAYS
        ? dayKeyRange(startOfWeekKey(anchorKey), WEEK_DAYS)
        : dayKeyRange(anchorKey, weekDays);
    }
    if (mode === 'day') return [anchorKey];
    return dayKeyRange(anchorKey, AGENDA_DAYS);
  })();
  const startKey = days[0] ?? anchorKey;
  const lastKey = days[days.length - 1] ?? anchorKey;
  return { days, startKey, endKey: addDayKey(lastKey, 1) };
}

/** The ISO instants bounding a view range in the viewer's zone. */
export function rangeInstants(range: ViewRange, zone: string): { start: string; end: string } {
  return {
    start: startOfZonedDay(range.startKey, zone).toISOString(),
    end: startOfZonedDay(range.endKey, zone).toISOString(),
  };
}

export function stepAnchor(
  mode: CalendarViewMode,
  anchorKey: DayKey,
  direction: 1 | -1,
  weekDays: number = WEEK_DAYS,
): DayKey {
  if (mode === 'month') return addMonthKey(anchorKey, direction);
  if (mode === 'week') return addDayKey(anchorKey, weekDays * direction);
  if (mode === 'agenda') return addDayKey(anchorKey, AGENDA_DAYS * direction);
  return addDayKey(anchorKey, direction);
}

export interface PeriodTitleOptions {
  weekDays?: number;
  /**
   * The phone app bar's form. The title shares one 390-px row with ☰, ‹ ›,
   * Today and the view picker, so "September 2026" becomes "Sep 2026", a day
   * and a week drop their year, and the agenda is titled by its month.
   */
  short?: boolean;
}

/** The toolbar's period title, which differs per view. */
export function periodTitle(
  mode: CalendarViewMode,
  anchorKey: DayKey,
  zone: string,
  { weekDays = WEEK_DAYS, short = false }: PeriodTitleOptions = {},
): string {
  if (mode === 'month') {
    return short ? fmtShortMonthYearOf(anchorKey, zone) : fmtMonthYearOf(anchorKey, zone);
  }
  if (mode === 'day') {
    return short
      ? `${fmtWeekdayShortOf(anchorKey, zone)}, ${fmtMonthDayOf(anchorKey, zone)}`
      : fmtFullDateOf(anchorKey, zone);
  }
  if (mode === 'agenda') {
    // Thirty days have no short range; the month they start in is the title
    // Google Calendar's phone agenda uses too.
    if (short) return fmtShortMonthYearOf(anchorKey, zone);
    return `${fmtMonthDayOf(anchorKey, zone)} – ${fmtMonthDayOf(addDayKey(anchorKey, AGENDA_DAYS - 1), zone)}`;
  }
  const { startKey: start, days } = rangeFor('week', anchorKey, weekDays);
  const end = days[days.length - 1] ?? start;
  if (short) return fmtShortRangeOf(start, end, zone);
  return `${fmtMonthDayOf(start, zone)} – ${fmtMonthDayOf(end, zone)}, ${partsOfDayKey(start).year}`;
}

/*
 * The two short shapes `zone.ts` does not have. Cached per zone like its own
 * formatters: an `Intl.DateTimeFormat` is expensive to build and the toolbar
 * re-renders on every step.
 */
const shortFormats = new Map<string, Intl.DateTimeFormat>();

function shortFormat(zone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = dateLocale();
  const key = `${locale ?? ''}|${zone}|${JSON.stringify(options)}`;
  let format = shortFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone });
    shortFormats.set(key, format);
  }
  return format;
}

/** "Sep 2026". */
function fmtShortMonthYearOf(key: DayKey, zone: string): string {
  return shortFormat(zone, { month: 'short', year: 'numeric' }).format(startOfZonedDay(key, zone));
}

/** "Sep 19 – 21", "Sep 29 – Oct 1": the locale's own range, month once. */
function fmtShortRangeOf(start: DayKey, end: DayKey, zone: string): string {
  return shortFormat(zone, { month: 'short', day: 'numeric' }).formatRange(
    startOfZonedDay(start, zone),
    startOfZonedDay(end, zone),
  );
}

/* ------------------------------------------------------------ all-day days */

const HALF_DAY_MS = 12 * 60 * 60 * 1000;

/**
 * Which calendar date an **all-day** boundary names.
 *
 * An all-day VEVENT carries a `DATE` value, which by RFC 5545 has no zone at
 * all — but the contract types `start`/`end` as instants, so whoever serialised
 * it had to pick a midnight. Different producers pick differently: the mock
 * adapter writes local midnight, a Go backend will almost certainly write UTC
 * midnight, and an imported feed may carry the calendar's own zone.
 *
 * Rather than guess the producer, this reads the date the instant is *nearest
 * to the middle of*: shift by twelve hours and take the UTC date. That returns
 * the authored date for any producer whose zone is within ±12 hours of UTC,
 * which is all of them except UTC+13/+14 (Pacific/Auckland in summer, Kiritimati).
 * Those are a known, documented edge; the alternative — treating an all-day
 * event as a timed one — shows "Vacation" starting the evening before for every
 * viewer west of the producer, which is the bug users actually notice.
 */
export function allDayKeyOf(instant: Date): DayKey {
  return dayKeyInZone(new Date(instant.getTime() + HALF_DAY_MS), 'UTC');
}

/**
 * The day keys an event occupies in the viewer's zone.
 *
 * All-day events are placed by date (above); timed events are placed by where
 * their instants fall on the viewer's own clock, which is the whole point of
 * rendering in the viewer's zone.
 */
export function dayKeysOfEvent(event: CalendarEvent, zone: string): DayKey[] {
  const start = new Date(event.start);
  const end = new Date(event.end);

  if (event.allDay) {
    const first = allDayKeyOf(start);
    // `DTEND` is exclusive for a DATE value (RFC 5545 §3.6.1): a one-day event
    // ends the next day. A producer that sends an inclusive end would collapse
    // to zero days, so one day is the floor.
    const lastExclusive = allDayKeyOf(end);
    const out: DayKey[] = [first];
    let cursor = addDayKey(first, 1);
    let guard = 0;
    while (cursor < lastExclusive && guard < 400) {
      out.push(cursor);
      cursor = addDayKey(cursor, 1);
      guard += 1;
    }
    return out;
  }

  const first = dayKeyInZone(start, zone);
  // A zero-length event still occupies its own day; an end exactly at midnight
  // belongs to the day before, which is what stepping back a millisecond gives.
  const lastInstant = end.getTime() > start.getTime() ? new Date(end.getTime() - 1) : start;
  const last = dayKeyInZone(lastInstant, zone);
  const out: DayKey[] = [first];
  let cursor = addDayKey(first, 1);
  let guard = 0;
  while (cursor <= last && guard < 400) {
    out.push(cursor);
    cursor = addDayKey(cursor, 1);
    guard += 1;
  }
  return out;
}

/* --------------------------------------------------------- grid geometry */

export interface DayGeometry {
  /** Real minutes this calendar day lasts: 1380, 1440 or 1500. */
  length: number;
  /** Fraction 0…1 of the day at which an instant falls, clamped. */
  fractionAt: (instant: Date) => number;
  /** Minutes from local midnight, unclamped (can be negative or > length). */
  minutesAt: (instant: Date) => number;
  /** The instant a fraction of the day's height corresponds to. */
  instantAtFraction: (fraction: number) => Date;
}

/**
 * The geometry of one day column.
 *
 * Positions divide by the day's **real** length, not by a nominal 1440. On the
 * 25-hour fall-back day that is what keeps 01:30 EDT and 01:30 EST — the same
 * wall time, an hour apart in the world — on two different rows instead of
 * stacked on one.
 */
export function dayGeometry(key: DayKey, zone: string): DayGeometry {
  const length = minutesInZonedDay(key, zone);
  const startMs = startOfZonedDay(key, zone).getTime();
  const minutesAt = (instant: Date): number => minutesFromDayStart(instant, key, zone);
  return {
    length,
    minutesAt,
    fractionAt: (instant) => Math.min(1, Math.max(0, minutesAt(instant) / length)),
    instantAtFraction: (fraction) =>
      new Date(startMs + Math.min(1, Math.max(0, fraction)) * length * 60_000),
  };
}

/** Snap an instant to the nearest `SNAP_MINUTES` boundary of real time. */
export function snapInstant(instant: Date, minutes: number = SNAP_MINUTES): Date {
  const step = minutes * 60_000;
  return new Date(Math.round(instant.getTime() / step) * step);
}

export interface HourLine {
  /** Real hours since the day began — the key, and the row order. */
  index: number;
  /** The instant of that gridline, for labelling in the viewer's zone. */
  instant: Date;
  /** 0…1 down the column. */
  fraction: number;
}

/**
 * The hour gridlines of a day, walked by **real** elapsed hours.
 *
 * Walking real hours rather than wall-clock hours means the spring-forward day
 * simply has 23 lines and the fall-back day 25. Nothing tries to draw an hour
 * that does not exist, nothing draws two lines on one pixel, and the labels
 * read `… 1 AM, 1 AM, 2 AM …` on the fall-back day — which is exactly what
 * happened.
 */
export function hourLines(key: DayKey, zone: string): HourLine[] {
  const geometry = dayGeometry(key, zone);
  const startMs = startOfZonedDay(key, zone).getTime();
  const out: HourLine[] = [];
  for (let index = 0; index < geometry.length / 60; index += 1) {
    const instant = new Date(startMs + index * 60 * 60_000);
    out.push({ index, instant, fraction: geometry.minutesAt(instant) / geometry.length });
  }
  return out;
}

/* ------------------------------------------------------------- formatting */

/** Human range for an event, collapsing same-day ends and all-day events. */
export function formatEventRange(event: CalendarEvent, zone: string): string {
  const start = new Date(event.start);
  const end = new Date(event.end);

  if (event.allDay) {
    const days = dayKeysOfEvent(event, zone);
    const first = days[0] ?? allDayKeyOf(start);
    const last = days[days.length - 1] ?? first;
    return first === last
      ? `${fmtFullDateOf(first, zone)} · all day`
      : `${fmtMonthDayOf(first, zone)} – ${fmtMonthDayOf(last, zone)} · all day`;
  }

  const startKey = dayKeyInZone(start, zone);
  const endKey = dayKeyInZone(new Date(Math.max(end.getTime() - 1, start.getTime())), zone);
  if (startKey === endKey) {
    return `${fmtFullDateOf(startKey, zone)} · ${fmtTimeIn(start, zone)} – ${fmtTimeIn(end, zone)}`;
  }
  return `${fmtMonthDayOf(startKey, zone)} ${fmtTimeIn(start, zone)} – ${fmtMonthDayOf(endKey, zone)} ${fmtTimeIn(end, zone)}`;
}

/** `"09:00 – 09:15"`, or `"All day"`. */
export function formatEventClock(event: CalendarEvent, zone: string): string {
  if (event.allDay) return 'All day';
  return `${fmtTimeIn(new Date(event.start), zone)} – ${fmtTimeIn(new Date(event.end), zone)}`;
}

export type { DayKey };
